import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';

/**
 * BalanceCard — shown in the sidebar above the profile menu.
 *
 * Layout (per Phase 2 spec — match the reference image):
 *
 *   ┌──────────────────────────────────┐
 *   │ Current plan                [+] │   <- top row
 *   │  Enterprise                       │   <- plan name (caps, brand color)
 *   │                                  │
 *   │ Credits used    Credits left     │   <- stats row
 *   │   50,154         5,846            │
 *   └──────────────────────────────────┘
 *
 * The whole card is clickable → /billing.
 * The [+] button is a separate, explicit "top up" affordance.
 */
export function BalanceCard({ billing }) {
  const navigate = useNavigate();

  if (!billing) {
    return (
      <div className="px-3 pt-3">
        <div className="rounded-lg bg-surface-2 border border-border p-3 animate-pulse">
          <div className="h-3 w-16 bg-surface-3 rounded mb-2" />
          <div className="h-4 w-24 bg-surface-3 rounded mb-3" />
          <div className="h-3 w-20 bg-surface-3 rounded" />
        </div>
      </div>
    );
  }

  const planName = billing.plan_display_name || billing.plan_code || 'Free';
  const creditsLeft = billing.credit_balance ?? 0;
  const creditsUsed = billing.total_consumed ?? 0;

  function go() {
    navigate('/billing');
  }

  return (
    <div className="px-3 pt-3">
      <div
        role="button"
        tabIndex={0}
        onClick={go}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') go(); }}
        aria-label="Open billing and top up credits"
        className="group customBtnnn relative w-full rounded-lg bg-surface-2 border border-border hover:border-brand/40 hover:bg-surface-3 transition-colors cursor-pointer p-3"
      >
        {/* Top row: plan label + plus button */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-2xs uppercase tracking-wider text-fg-muted">
              Current plan
            </div>
            <div className="text-xs font-semibold text-brand uppercase tracking-wider mt-0.5 truncate">
              {planName}
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(); }}
            aria-label="Top up credits"
            title="Top up credits"
            className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-brand text-brand-fg shadow-sm hover:bg-brand-strong transition-colors shrink-0"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {/* Divider */}
        <div className="border-t border-border my-2.5" />

        {/* Bottom row: credits used + left */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-2xs uppercase tracking-wider text-fg-muted">
              Credits used
            </div>
            <div className="text-sm font-semibold text-fg tabular-nums mt-0.5">
              {creditsUsed.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-2xs uppercase tracking-wider text-fg-muted">
              Credits left
            </div>
            <div className="text-sm font-semibold text-fg tabular-nums mt-0.5">
              {creditsLeft.toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}